import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _excluded = ["iconOnly", "align", "as", "children", "variant", "size", "icon", "iconRight", "iconScale", "fullWidth", "title", "textOverflow", "isLoading", "isDisabled", "aria-label", "loadingLabel"];
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.includes.js";
import React, { forwardRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { alpha, mix, getSolidBackgroundShade, isDark, pxToRem, _3px } from 'utils/ui';
import { fillParent, ellipsis, overflowWrap } from 'styles/mixins';
import { alignMap } from 'styles/styleProps/flexProps';
import { getPropFilter, getPropNamesFromPropDefinition } from 'utils/ui/styleProps';
import positionProps, { positionPropsDef } from 'styles/styleProps/positionProps';
import marginProps, { marginPropsDef } from 'styles/styleProps/marginProps';
import ButtonCore from 'components/ButtonCore';
import Icon from 'components/Icon';
import VisuallyHidden from 'components/VisuallyHidden';
var stylePropNames = getPropNamesFromPropDefinition([].concat(_toConsumableArray(positionPropsDef), _toConsumableArray(marginPropsDef)));
var shouldForwardProp = getPropFilter(['round', 'square', 'fullWidth', 'isLoading', 'variant', 'size', 'align'].concat(_toConsumableArray(stylePropNames)));
var BUTTON_HEIGHT_MAP = {
  small: 25,
  medium: 33,
  default: 42,
  large: 56
};
var Wrapper = styled(ButtonCore).withConfig({
  shouldForwardProp: shouldForwardProp
}).withConfig({
  displayName: "Button__Wrapper",
  componentId: "sc-cuikcm-0"
})(["", " display:inline-flex;align-items:center;justify-content:", ";", " max-width:100%;", " font-size:", ";font-weight:600;line-height:1.125;text-align:", ";text-decoration:none;min-height:", ";padding-inline:", ";", " ", " ", " ", " ", " ", " ", " ", " ", " &.focus-visible{outline:none;}&.is-disabled{", " cursor:", ";", "}"], positionProps, function (p) {
  return alignMap[p.align || 'center'];
}, function (p) {
  return p.fullWidth && css(["display:flex;width:100%;"]);
}, marginProps, function (p) {
  return p.theme.globals.typeScale.m;
}, function (p) {
  return p.align;
}, function (p) {
  return pxToRem(BUTTON_HEIGHT_MAP[p.size || 'default']);
}, pxToRem(12), function (p) {
  return p.size === 'small' && css(["padding-inline:", ";font-size:", ";"], pxToRem(5), function (p) {
    return p.theme.globals.typeScale.xs;
  });
}, function (p) {
  return p.size === 'medium' && css(["padding-inline:", ";font-size:", ";"], pxToRem(8), function (p) {
    return p.theme.globals.typeScale.s;
  });
}, function (p) {
  return p.size === 'large' && css(["padding-inline:", ";"], pxToRem(19));
}, function (p) {
  return !p.square && css(["border-radius:", ";"], p.round ? '2rem' : pxToRem(p.theme.globals.borderRadius.buttons));
}, function (p) {
  return (p.variant === 'default' || p.variant === 'primary' || p.variant === 'important') && css(["color:", ";background-color:", ";"], function (p) {
    return p.theme.globals.buttons[p.variant].text;
  }, function (p) {
    return p.theme.globals.buttons[p.variant].background;
  });
}, function (p) {
  return p.variant === 'default-negative' && css(["color:", ";background-color:", ";"], function (p) {
    return p.theme.globals.colorBlocks.negative;
  }, function (p) {
    return p.theme.globals.buttons.default.background;
  });
}, function (p) {
  return p.variant === 'transparent' && css(["color:", ";background-color:transparent;"], function (p) {
    return p.theme.links;
  });
}, function (p) {
  return p.variant === 'shaded' && css(["color:", ";background-color:", ";"], function (p) {
    return p.theme.links;
  }, function (p) {
    return alpha(p.theme.shade, p.theme.shadeStrength);
  });
}, function (p) {
  return (p.variant === 'default' || p.variant === 'default-negative') && css(["&:not(.is-disabled)::before{content:'';", " border-radius:inherit;border:1px solid ", ";}"], fillParent, function (p) {
    return alpha(p.theme.shade, p.theme.lineStrength);
  });
}, function (p) {
  var darkColor = mix(p.theme.text)('white', p.theme.textDimStrength * 0.75);
  var lightColor = getSolidBackgroundShade(p.theme);
  if (p.variant === 'primary' || p.variant === 'important') {
    var isTextDark = isDark(p.theme.globals.buttons[p.variant].text);
    return "\n\t\t\t\tcolor: ".concat(isTextDark ? darkColor : lightColor, ";\n\t\t\t\tbackground-color: ").concat(isTextDark ? lightColor : darkColor, ";\n\t\t\t\t");
  } else {
    return "\n\t\t\t\tcolor: ".concat(darkColor, ";\n\t\t\t\tbackground-color: ").concat(lightColor, ";\n\t\t\t\t");
  }
}, function (p) {
  return p.isLoading ? 'wait' : 'not-allowed';
}, function (p) {
  return (p.variant === 'transparent' || p.variant === 'shaded') && css(["background-color:transparent;border-color:transparent;"]);
});
var FocusRing = styled.span.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'variant';
  }
}).withConfig({
  displayName: "Button__FocusRing",
  componentId: "sc-cuikcm-1"
})(["", " border-radius:inherit;box-shadow:0 0 0 ", " ", ";pointer-events:none;opacity:0;transition:opacity 250ms linear;will-change:opacity;", ".focus-visible > &&{top:-", ";left:-", ";bottom:-", ";right:-", ";box-shadow:0 0 0 ", " ", ";opacity:1;transition-duration:50ms;}", ":not(.is-disabled):active > &&{opacity:1;transition-duration:50ms;}"], fillParent, _3px, function (p) {
  return alpha(p.theme.shade, p.theme.lineStrength + 0.05);
}, Wrapper, _3px, _3px, _3px, _3px, _3px, function (p) {
  return p.variant === 'primary' || p.variant === 'important' ? p.theme.globals.buttons[p.variant].background : p.theme.links;
}, Wrapper);
var HoverShade = styled.span.withConfig({
  displayName: "Button__HoverShade",
  componentId: "sc-cuikcm-2"
})(["", " border-radius:inherit;background-color:", ";pointer-events:none;opacity:0;transition:opacity 250ms linear;will-change:opacity;", ".is-active > &{opacity:0.666;box-shadow:inset 0 0 0.25rem rgba(0,0,0,0.5);}", ":not(.is-disabled):hover > &&{opacity:1;transition-duration:50ms;}", ":not(.is-disabled):active > &&{opacity:0;transition-duration:250ms;}"], fillParent, function (p) {
  return alpha(p.theme.shade, Math.min(p.theme.shadeStrength, 0.12));
}, Wrapper, Wrapper, Wrapper);

/**
 * The button label should just have some horizontal spacing.
 * But if `textOverflow="ellipsis" is set, the Button label
 * will have overflow set to "hidden", which can cause the
 * descenders of some characters (g, y, j) to be cut off.
 * To avoid this, we also give the text label some vertical
 * padding and visually remove it using negative margins.
 */

var ButtonText = styled.span.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['textOverflow', 'px'].includes(prop);
  }
}).withConfig({
  displayName: "Button__ButtonText",
  componentId: "sc-cuikcm-3"
})(["padding:", " ", ";vertical-align:middle;", " z-index:0;"], function (p) {
  return p.theme.globals.spacing.xxs;
}, function (p) {
  return p.theme.globals.spacing[p.px];
}, function (p) {
  return p.textOverflow === 'ellipsis' ? ellipsis : overflowWrap;
});
var spin = keyframes(["0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}"]);
var SpinningIcon = styled(Icon).withConfig({
  displayName: "Button__SpinningIcon",
  componentId: "sc-cuikcm-4"
})(["animation:", " 0.6s infinite linear both;"], spin);
var ButtonIcon = function ButtonIcon(_ref) {
  var isLoading = _ref.isLoading,
    icon = _ref.icon,
    iconScale = _ref.iconScale;
  if (isLoading) {
    return /*#__PURE__*/React.createElement(SpinningIcon, {
      disablePointerEvents: true,
      name: "spinnerdot",
      scale: iconScale
    });
  }
  if (typeof icon === 'string') {
    return /*#__PURE__*/React.createElement(Icon, {
      disablePointerEvents: true,
      name: icon,
      scale: iconScale
    });
  } else {
    return icon;
  }
};
export var Button = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var iconOnly = _ref2.iconOnly,
    _ref2$align = _ref2.align,
    align = _ref2$align === void 0 ? 'center' : _ref2$align,
    as = _ref2.as,
    children = _ref2.children,
    _ref2$variant = _ref2.variant,
    variant = _ref2$variant === void 0 ? 'default' : _ref2$variant,
    size = _ref2.size,
    icon = _ref2.icon,
    iconRight = _ref2.iconRight,
    iconScale = _ref2.iconScale,
    fullWidth = _ref2.fullWidth,
    title = _ref2.title,
    _ref2$textOverflow = _ref2.textOverflow,
    textOverflow = _ref2$textOverflow === void 0 ? 'wrap' : _ref2$textOverflow,
    isLoading = _ref2.isLoading,
    isDisabled = _ref2.isDisabled,
    ariaLabel = _ref2['aria-label'],
    loadingLabelProp = _ref2.loadingLabel,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var Component = as || 'button';
  var loadingLabel = loadingLabelProp || t('loading');
  return (
    /*#__PURE__*/
    /*
     Warning!!! `Wrapper` is currently not being properly typechecked.
     Comment out `otherProps` and `as` when making changes to check types 
    */
    React.createElement(Wrapper, _extends({
      ref: ref,
      forwardedAs: Component,
      position: "relative",
      "aria-label": ariaLabel || title,
      variant: variant,
      size: size,
      align: align,
      fullWidth: fullWidth
    }, otherProps, {
      "aria-live": isLoading !== undefined ? 'polite' : undefined,
      isDisabled: isDisabled || isLoading,
      isLoading: isLoading
    }), /*#__PURE__*/React.createElement(HoverShade, null), /*#__PURE__*/React.createElement(FocusRing, {
      variant: variant
    }), icon && /*#__PURE__*/React.createElement(ButtonIcon, {
      isLoading: isLoading && (!iconRight || iconOnly),
      icon: icon,
      iconScale: iconScale
    }), children && (iconOnly ? /*#__PURE__*/React.createElement(VisuallyHidden, null, children, isLoading && loadingLabel) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ButtonText, {
      textOverflow: textOverflow,
      px: size === 'large' ? 'xs' : 'xxs'
    }, children), isLoading && /*#__PURE__*/React.createElement(VisuallyHidden, null, loadingLabel), !icon && !iconRight && isLoading && /*#__PURE__*/React.createElement(SpinningIcon, {
      disablePointerEvents: true,
      name: "spinnerdot",
      scale: iconScale
    }))), iconRight && /*#__PURE__*/React.createElement(ButtonIcon, {
      isLoading: isLoading,
      icon: iconRight,
      iconScale: iconScale
    }))
  );
});
Button.displayName = 'Button';
export default Button;